import React, { useState } from 'react';
import axios from 'axios';
import {
  MapPin,
  Mail,
  Phone,
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset after showing success
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
      }, 5000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setIsSubmitting(false);
      setError('Failed to send message. Please try again or contact us directly.');
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-0.5 bg-burgundy" />
            <span className="text-burgundy text-sm tracking-widest uppercase font-medium">
              Get In Touch
            </span>
            <div className="w-12 h-0.5 bg-burgundy" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
            Call Us Today
          </h2>
          <p className="text-slate text-lg max-w-2xl mx-auto">
            Partner with Nora Gold Advisory to strengthen your strategy, build
            investment-ready solutions, and unlock sustainable growth.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-charcoal mb-8">
              Contact Information
            </h3>

            <div className="space-y-6 mb-10">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-burgundy/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-burgundy" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-charcoal mb-1">Address</h4>
                  <p className="text-slate leading-relaxed">
                    {contactInfo.building}
                    <br />
                    {contactInfo.street}
                    <br />
                    {contactInfo.area}
                    <br />
                    Digital Address: {contactInfo.digitalAddress}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-burgundy/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-burgundy" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-charcoal mb-1">Email</h4>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-burgundy hover:text-gold transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-burgundy/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-burgundy" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-charcoal mb-1">Phone</h4>
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="text-burgundy hover:text-gold transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Postal Address */}
            <div className="p-6 bg-charcoal">
              <h4 className="font-semibold text-gold mb-2">Postal Address</h4>
              <p className="text-ivory/80">{contactInfo.postalAddress}</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 border border-slate/10 shadow-lg">
            <h3 className="text-2xl font-bold text-charcoal mb-6">
              Send Us a Message
            </h3>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 bg-green-100 flex items-center justify-center mb-6 rounded-full">
                  <CheckCircle className="text-green-600" size={40} />
                </div>
                <h4 className="text-xl font-bold text-charcoal mb-2">
                  Message Sent!
                </h4>
                <p className="text-slate">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-charcoal">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="border-slate/20 focus:border-burgundy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-charcoal">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="border-slate/20 focus:border-burgundy"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-charcoal">
                      Company/Organization
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="border-slate/20 focus:border-burgundy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-charcoal">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+233 XX XXX XXXX"
                      className="border-slate/20 focus:border-burgundy"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-charcoal">
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your business needs and how we can help..."
                    rows={5}
                    className="border-slate/20 focus:border-burgundy resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-burgundy hover:bg-burgundy/90 text-ivory py-6 text-lg font-semibold transition-all duration-300 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
